import { Switch } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerModal } from '../../../components/custom_components/Modal'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'
import { disableRanking, enableRanking } from '../../../graphql/talents-season/useRankingMutations'
import { Country } from '../../../types'
import { showNotification } from '../../../components/custom_components/Notification/notification'

export const RankingToggle: FC<{ isRankingEnabled: boolean; companyId: string; country: Country }> = ({
  isRankingEnabled,
  companyId,
  country,
}) => {
  const { t } = useTranslation()
  const [enableRankingModalVisible, setEnableRankingModalVisible] = useState<boolean>(false)
  const [disableRankingModalVisible, setDisableRankingModalVisible] = useState<boolean>(false)
  const [switchStatus, setSwitchStatus] = useState<boolean>(isRankingEnabled)
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  return (
    <>
      <EnableRankingModal
        isOpen={enableRankingModalVisible}
        isSubmitting={isSubmitting}
        companyId={companyId}
        country={country}
        onClose={() => {
          setError(undefined)
          setEnableRankingModalVisible(false)
        }}
        onSubmit={async () => {
          toggleSubmitting(true)
          const { errors } = await enableRanking(companyId, country)
          if (!!errors && !!errors.length) {
            setError(errors[0].message)
          } else {
            showNotification({
              type: 'success',
              message: t('companySettingsView.rankingTab.rankingModal.notifications.enable.success.title'),
              description: t('companySettingsView.rankingTab.rankingModal.notifications.enable.success.description'),
            })
          }
          toggleSubmitting(false)
          setSwitchStatus(true)
        }}
        error={error}
      />
      <DisableRankingModal
        isOpen={disableRankingModalVisible}
        isSubmitting={isSubmitting}
        companyId={companyId}
        country={country}
        onClose={() => {
          setError(undefined)
          setDisableRankingModalVisible(false)
        }}
        onSubmit={async () => {
          toggleSubmitting(true)
          const { errors } = await disableRanking(companyId, country)
          if (!!errors && !!errors.length) {
            setError(errors[0].message)
          } else {
            showNotification({
              type: 'success',
              message: t('companySettingsView.rankingTab.rankingModal.notifications.disable.success.title'),
              description: t('companySettingsView.rankingTab.rankingModal.notifications.disable.success.description'),
            })
          }
          toggleSubmitting(false)
          setSwitchStatus(false)
        }}
        error={error}
      />
      <BackgroundBox noPadding mb="xxs" r className="per-tabbarContent--height">
        <PerFlexRow justify="start" align="middle" noWrap className="fullHeight" mr="xs" ml="xs">
          <PerText>{t('companySettingsView.rankingTab.enableRanking')}</PerText>
          <PerSpacer ml="m" />
          <Switch
            data-testid="rankingToggle"
            checked={switchStatus}
            onChange={() => {
              isRankingEnabled ? setDisableRankingModalVisible(true) : setEnableRankingModalVisible(true)
            }}
          />
        </PerFlexRow>
      </BackgroundBox>
    </>
  )
}

type RankingModalProps = {
  isOpen: boolean
  isSubmitting: boolean
  companyId: string
  country: Country
  onClose: () => void
  onSubmit: () => void
  error?: string
}

const EnableRankingModal: FC<RankingModalProps> = ({ isOpen, isSubmitting, error, onClose, onSubmit }) => {
  const { t } = useTranslation()
  return (
    <>
      <PerModal
        size="large"
        title={{ heading: t('companySettingsView.rankingTab.rankingModal.enableRanking') }}
        centered
        visible={isOpen}
        onCancel={() => {
          onClose()
        }}
        footer={{
          cancelBtn: {
            onClick: () => {
              onClose()
            },
          },
          customBtns: [
            {
              text: t('companySettingsView.rankingTab.rankingModal.enableRanking'),
              onClick: () => {
                onSubmit()
                onClose()
              },
              color: 'success',
              icon: 'check_stroke',
              isProcessing: { active: isSubmitting },
            },
          ],
        }}
      >
        <PerText mb="s">{t('companySettingsView.rankingTab.rankingModal.enableRankingDescription')} </PerText>
        {error && (
          <PerText color="error" fontSize="m" mt="m">
            {error}
          </PerText>
        )}
      </PerModal>
    </>
  )
}

const DisableRankingModal: FC<RankingModalProps> = ({ isOpen, isSubmitting, error, onClose, onSubmit }) => {
  const { t } = useTranslation()
  return (
    <>
      <PerModal
        size="large"
        title={{ heading: t('companySettingsView.rankingTab.rankingModal.disableRanking') }}
        centered
        visible={isOpen}
        onCancel={() => {
          onClose()
        }}
        footer={{
          cancelBtn: {
            onClick: () => {
              onClose()
            },
          },
          customBtns: [
            {
              text: t('companySettingsView.rankingTab.rankingModal.disableRanking'),
              onClick: () => {
                onSubmit()
                onClose()
              },
              color: 'success',
              icon: 'check_stroke',
              isProcessing: { active: isSubmitting },
            },
          ],
        }}
      >
        <PerText mb="s">{t('companySettingsView.rankingTab.rankingModal.disableRankingDescription')} </PerText>
        {error && (
          <PerText color="error" fontSize="m" mt="m">
            {error}
          </PerText>
        )}
      </PerModal>
    </>
  )
}
